import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  useOutsideClick,
  useToast,
} from '@chakra-ui/react';
import classNames from 'classnames';
import {
  forwardRef,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { TNode } from '../../Node';
import type { TreeSelectionComponentProps } from '../../TreeSelection';
import {
  type TreeApi,
  type TreeApiBaseProps,
  type TreeApiControlProps,
  useTreeApi,
} from '../../useTreeApi';
import { searchMatch } from '../../utils/search';
import { BranchLabel } from '../BranchLabel';
import styles from '../PopoutTree.module.css';
import { useDrillMode } from '../useDrillMode';
import { SplitResultsTree } from './SplitResultsTree';
import { type Branch, UnifiedPopoutTree } from './UnifiedPopoutTree';
import { useCompanySearch } from './useCompanySearchV2';

const TREE_HEIGHT = 230;
const TOP_SECTION_HEIGHT = 24;

const HEIGHT = TREE_HEIGHT - TOP_SECTION_HEIGHT;

export type AsyncCompanySearchTreeProps = Omit<
  TreeSelectionComponentProps,
  'treeApi' | 'drillMode' | 'onClick'
> &
  Omit<TreeApiBaseProps, 'openByDefault'> &
  TreeApiControlProps & {
    branches?: Branch[] | ((node: TNode) => Branch);
    disabledLevels?: number[];
    searchInputRef?: React.RefObject<HTMLInputElement>;
    selectedNodeData?: TNode[];
    isOpen?: boolean;
    middleContent?: React.ReactNode;
  };

export const AsyncCompanySearchTree = forwardRef(
  (
    {
      branches,
      disabledLevels,
      className,
      sortSuggestedSearch,
      searchInputRef,
      selectedNodeData = [],
      middleContent,
      ...props
    }: AsyncCompanySearchTreeProps,
    ref: Ref<TreeApi | undefined>
  ) => {
    const companyIndustryTreeRef = useRef<{
      scrollToTop: () => void;
    }>();

    const { data } = props;

    const unifiedTreeApi = useTreeApi({
      ...props,
      data,
      openByDefault: false,
    });

    const { search, onSearch } = unifiedTreeApi;

    const {
      data: searchResults,
      rawMappedResults,
      initialIndustries,
      isLoading,
      error,
      inputValue,
      setInputValue,
      handleChange,
    } = useCompanySearch(search, onSearch, data, selectedNodeData);

    useImperativeHandle(ref, () => {
      return {
        ...unifiedTreeApi,
        onResetSearch,
        scrollToTop: () => companyIndustryTreeRef.current?.scrollToTop(),
      };
    });

    const [branchLabel, setBranchLabel] = useState<Branch | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    const toast = useToast();

    useEffect(() => {
      if (error) {
        toast({
          title: 'Search error',
          description:
            'Something went wrong searching all companies. Showing results from a limited set of companies. Please try again later.',
          status: 'error',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }, [error, toast]);

    const popoutTreeRef = useRef<HTMLDivElement>(null);

    const isSearchEmpty = search.length === 0;

    const isDrillEnabled = isSearchEmpty;

    const {
      drillState,
      drillIntoNode,
      drillOutToLevel,
      isDrilledInto,
      clearDrillState,
    } = useDrillMode();

    const onResetSearch = () => {
      setInputValue('');
      onSearch('');
    };

    useOutsideClick({
      ref: popoutTreeRef,
      handler: () => {
        if (!isSearchEmpty) return;

        clearDrillState();
      },
    });

    useEffect(() => {
      if (
        typeof branches === 'function' &&
        unifiedTreeApi.treeRef?.current?.root
      ) {
        setBranchLabel(branches(unifiedTreeApi.treeRef.current.root));
      } else if (Array.isArray(branches) && branches[0]) {
        setBranchLabel(branches[0]);
      }
    }, [branches, unifiedTreeApi.treeRef]);

    const isSearchMatch = (node: TNode, search: string) => {
      return searchMatch(node, search);
    };

    return (
      <div
        className={classNames(styles.popoutTree, className)}
        ref={popoutTreeRef}
      >
        <InputGroup
          size="sm"
          marginBottom="4px"
          onClick={() => {
            clearDrillState();
          }}
        >
          <Input
            value={inputValue}
            onChange={handleChange}
            placeholder="Search..."
            data-testid="tree-search-input"
            autoFocus
            ref={searchInputRef}
          />
          {isLoading && (
            <InputRightElement mr={6}>
              <Flex alignItems="center">
                <Spinner
                  w={3.5}
                  h={3.5}
                  color="green.600"
                  data-testid="async-tree-search-input-loading"
                  mr={1}
                />
              </Flex>
            </InputRightElement>
          )}
          <InputRightElement>
            {isSearchEmpty ? (
              <SearchIcon w={3.5} h={3.5} color="silver.600" />
            ) : (
              <CloseIcon
                data-testid="async-tree-search-input-clear-icon"
                style={{ cursor: 'pointer' }}
                w={2.5}
                h={2.5}
                color="silver.600"
                onClick={onResetSearch}
              />
            )}
          </InputRightElement>
        </InputGroup>

        {isSearchEmpty && branchLabel && (
          <Box
            height={`${TOP_SECTION_HEIGHT}px`}
            display="flex"
            alignItems="center"
            onClick={() => {
              clearDrillState();
            }}
          >
            <BranchLabel
              branch={branchLabel.label}
              branchDescription={branchLabel.description}
            />
          </Box>
        )}
        {middleContent && <Box>{middleContent}</Box>}
        <div className={styles.rootTreeContainer} data-tree-root>
          {isSearchEmpty ? (
            <UnifiedPopoutTree
              {...props}
              data={searchResults}
              treeApi={unifiedTreeApi}
              disabledLevels={disabledLevels}
              height={HEIGHT}
              drillMode={{
                isEnabled: isDrillEnabled,
                level: 0,
                isRoot: true,
                drillIntoNode,
                drillOutToLevel,
                ...(isDrillEnabled && { isDrilledInto }),
              }}
              sortSuggestedSearch={sortSuggestedSearch && !isSearchEmpty}
              drillState={drillState}
              branches={branches}
              isSearchMatch={isSearchMatch}
              isLoading={isLoading}
              onDrillStateChange={(level) => {
                drillOutToLevel(level);
              }}
              ref={companyIndustryTreeRef}
            />
          ) : (
            <SplitResultsTree
              {...props}
              companyResults={rawMappedResults}
              industryResults={initialIndustries}
              onCompanyChange={(ids, nodes) => {
                props.onChange?.(ids, nodes);
              }}
              onIndustryChange={(ids) => {
                props.onChange?.(ids);
              }}
              height={HEIGHT}
              isLoading={isLoading}
              search={search}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              disableIndustryTab={
                disabledLevels?.includes(0) && disabledLevels?.includes(1)
              }
            />
          )}
        </div>
      </div>
    );
  }
);
